<template>
  <div
    class="card card-body card-chart"
    :class="{'bg-light': selectResearchItemMode || groupByMode}"
  >
    <div v-if="selectResearchItemMode">
      <h4>
        {{ $t('phrase.select_questions') }}
      </h4>
      <table class="table">
        <tr
          v-for="researchItem in numericItems"
          :key="researchItem.id"
        >
          <td style="width: 5px;">
            <label :for="`research-item-${researchItem.id}`" class="m-0">
              <i
                class="fa"
                :class="{'fa-square': !selectedResearchItemIds.includes(researchItem.id),
                 'fa-check-square': selectedResearchItemIds.includes(researchItem.id)}"
              />
            </label>
          </td>
          <td>
            <label class="m-0">
              <input
                class="hidden"
                type="checkbox"
                :id="`research-item-${researchItem.id}`"
                :value="researchItem.id"
                v-model="selectedResearchItemIds"
              >
              <span>
                {{ researchItem.title }}
                <br/>
                <small>
                  <em v-if="researchItem.short_title">
                    {{ researchItem.short_title }}
                  </em>
                </small>
              </span>
            </label>
          </td>
        </tr>
        <tr>
          <td colspan="2">
            <v-button
              :disabled="selectedResearchItemIds.length === numericItems.length"
              text="phrase.select_all"
              style-type="link"
              size="sm"
              @click="selectAll"
            />
          </td>
        </tr>
      </table>
      <div class="d-flex justify-content-end">
        <v-button
          :disabled="selectedResearchItemIds.length === 0"
          text="word.show"
          style-type="dark"
          size="sm"
          @click="showChart"
        />
      </div>
    </div>
    <div v-else-if="groupByMode">
      <div class="d-flex justify-content-between">
        <h4>
          {{ $t('phrase.group_by') }}
        </h4>
        <div>
          <v-button
            text="word.back"
            style-type="link"
            size="sm"
            @click="groupByMode = false"
          />
        </div>
      </div>

      <table class="table">
        <tr
          v-for="researchItem in singleChoiceResearchItems"
          :key="researchItem.id"
        >
          <td>
            <strong>{{ researchItem.title }}</strong>
            <ul>
              <li
                v-for="option in researchItem.item.options"
                :key="option.value"
              >
                {{ option.value }}
              </li>
            </ul>
          </td>
          <td>
            <v-button
              text="word.select"
              size="sm"
              @click="selectGroupByItem(researchItem)"
            />
          </td>
        </tr>
      </table>
    </div>
    <div v-else class="h-100">
      <div
        v-if="loading"
        class="d-flex justify-content-center align-items-center h-100"
      >
        <v-loader />
      </div>
      <div v-else>
        <v-chart
          :key="chartKey"
          :title="$t('word.averages')"
          chart-type="bar"
          :labels="labels"
          :data-sets="selectedGroupByItem ? groupedDatasets : datasets"
          :extra-actions="extraActions"
          @select-questions="selectResearchItemMode = true"
          @make-groups="groupByMode = true"
          @reset-chart="resetChart"
        />
      </div>
    </div>
  </div>
</template>

<script>
import Button from '@/common/components/Button.vue';
import Chart from '@/organisation/components/dashboard/Chart.vue';
import Loader from '@/common/components/Loader.vue';
import { avg } from '@/common/utils/math';
import {
  cleanedItemText,
  isSingleChoice,
} from '@/common/services/items';
import { mapGetters } from 'vuex';

export default {
  name: 'BarChartWidget',
  components: {
    'v-button': Button,
    'v-chart': Chart,
    'v-loader': Loader,
  },
  methods: {
    cleanedItemText,
    showChart() {
      this.selectResearchItemMode = false;
    },
    selectAll() {
      this.selectedResearchItemIds = this.numericItems.map((item) => item.id);
    },
    selectGroupByItem(researchItem) {
      this.selectedGroupByItem = researchItem;
      this.groupByMode = false;
    },
    resetChart() {
      this.selectAll();
      this.selectedGroupByItem = null;
    },
  },
  computed: {
    ...mapGetters('researches', [
      'loadingResearchItems',
      'loadingResearchStatistics',
      'loadingResearchReviewSessions',
      'researchItems',
      'data',
    ]),
    loading() {
      return (
        this.loadingResearchItems
        || this.loadingResearchReviewSessions
        || this.loadingResearchStatistics
      );
    },
    chartKey() {
      let count = this.selectedResearchItemIds.length;
      if (this.selectedGroupByItem) {
        count += 1;
      }
      return count;
    },
    labels() {
      return this.selectedResearchItems
        .map((item) => cleanedItemText(item.short_title || item.title));
    },
    numericItems() {
      return this.researchItems.filter(
        (researchItem) => (researchItem.item.is_numeric && researchItem.show_in_dashboard),
      );
    },
    singleChoiceResearchItems() {
      return this.researchItems
        .filter((researchItem) => (
          isSingleChoice(researchItem.item.render_type) && researchItem.show_in_dashboard));
    },
    averages() {
      return this.selectedResearchItems.map((item) => {
        const answers = this.data.answersByResearchItem[item.id];
        if (answers) {
          return avg(answers.map((answer) => answer.value_num), 1);
        }
        return 0;
      });
    },
    groupedDatasets() {
      return this.selectedGroupByItem.item.options.map((option) => {
        const filteredSessions = this.data.sessions.filter((session) => {
          const answer = session.answerDict[this.selectedGroupByItem.id];
          return answer && answer.value_str === option.value;
        });
        const allAnswers = this.selectedResearchItems.map((researchItem) => (
          filteredSessions.map((session) => {
            const answer = session.answerDict[researchItem.id];
            return answer && answer.value_num;
          })));
        return {
          label: option.value,
          data: allAnswers.map((answers) => avg(answers.filter((answer) => (!!answer)), 1)),
          datalabels: {
            color: '#ffffff',
            opacity: 1,
            font: {
              weight: 'bold',
              size: 11,
            },
          },
        };
      });
    },
    datasets() {
      let datasets = [
        {
          label: this.$t('word.research_average'),
          data: this.averages,
          datalabels: {
            color: '#ffffff',
            opacity: 1,
            font: {
              weight: 'bold',
              size: 11,
            },
          },
        },
      ];

      if (this.showTotalAverages) {
        const totalAverages = this.selectedResearchItems.map((researchItem) => (
          this.data.statistics.platform_averages[researchItem.id]
        ));
        let showTotalAverages = false;
        totalAverages.forEach((average) => {
          if (average > 0) {
            showTotalAverages = true;
          }
        });
        if (showTotalAverages) {
          datasets = [
            ...datasets,
            {
              label: this.$t('word.total_average'),
              data: totalAverages.map((average) => (average ? average.toFixed(1) : 0)),
              datalabels: {
                color: '#ffffff',
                opacity: 1,
                font: {
                  weight: 'bold',
                  size: 11,
                },
              },
            },
          ];
        }
      }
      return datasets;
    },
    selectedResearchItems() {
      return this.numericItems.filter((item) => this.selectedResearchItemIds.includes(item.id));
    },
    showTotalAverages() {
      if (this.data.statistics.platform_averages) {
        return Object.keys(this.data.statistics.platform_averages).length > 0;
      }
      return false;
    },
    extraActions() {
      return [
        {
          icon: 'mouse-pointer',
          text: `${this.$t('phrase.select_questions')} (${this.selectedResearchItems.length}/${this.numericItems.length})`,
          name: 'select-questions',
          hidden: this.numericItems.length <= 1,
        },
        {
          icon: 'users',
          text: this.$t('phrase.make_groups'),
          name: 'make-groups',
          hidden: this.singleChoiceResearchItems.length === 0,
        },
        {
          icon: 'rotate-left',
          text: this.$t('phrase.reset_chart'),
          name: 'reset-chart',
        },
      ];
    },
  },
  data() {
    return {
      selectedResearchItemIds: [],
      selectedGroupByItem: null,
      selectResearchItemMode: false,
      groupByMode: false,
      showComparingResearchAverages: false,
      chartUpdated: 0,
    };
  },
  created() {
    this.selectedResearchItemIds = this.numericItems.map((item) => item.id);
  },
};
</script>
